import styled, { css } from 'styled-components';
import { compactTablet, tabletMin } from '../../constants';

export const HeroWrapper = styled.div`
  position: relative;
  isolation: isolate;
  width: 100%;

  /* Stretch to the compare-table row height so every card ends level. */
  @media ${tabletMin} {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
  }
`;

export const HeroCard = styled.div<{
  $gradient: string;
  $borderColor?: string | null;
  $borderGradient?: string | null;
}>`
  position: relative;
  z-index: 1;
  border: 1px solid ${({ $borderColor }) => $borderColor ?? 'transparent'};
  background: ${({ $gradient, $borderGradient }) =>
    $borderGradient
      ? `${$gradient} padding-box, ${$borderGradient} border-box`
      : $gradient};
  border-radius: 0.75rem;
  padding: 1.5rem;
  height: 10.75rem;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  /* Figma: Elevation/Light/raised-30 */
  box-shadow: 0 0.625rem 0.625rem -0.3125rem rgba(0, 0, 0, 0.04), 0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.1);

  /* In the compare table the column is narrow enough that the copy can outgrow the fixed
     height, which collides with the price row. Grow instead, and let the row equalise. */
  @media ${tabletMin} {
    height: auto;
    min-height: 10.75rem;
    flex: 1 1 auto;
  }

  @media ${compactTablet} {
    padding: 1rem;
  }
`;

export const HeroContent = styled.div<{ $hasPrice: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ $hasPrice }) => ($hasPrice ? '1rem' : '0.5rem')};
  flex: 1;
  min-width: 0;
  min-height: 0;
  width: 100%;

  @media ${tabletMin} {
    ${({ $hasPrice }) =>
      $hasPrice &&
      css`
        display: grid;
        grid-template-rows: 1fr auto;
        row-gap: 1rem;
      `}
  }

  @media ${compactTablet} {
    gap: ${({ $hasPrice }) => ($hasPrice ? '0.75rem' : '0.25rem')};
  }
`;

export const HeroTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media ${tabletMin} {
    align-self: start;
  }
`;

export const HeroTitle = styled.h2<{ $textColor: string }>`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 2rem;
  color: ${({ $textColor }) => $textColor};

  @media ${compactTablet} {
    font-size: 1rem;
    line-height: 1.5rem;
  }

  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    font-size: 1.25rem;
  }
`;

export const HeroSubtitle = styled.p<{ $textColor: string }>`
  margin: 0;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.25rem;
  color: ${({ $textColor }) => $textColor};

  @media ${compactTablet} {
    font-size: 0.75rem;
    line-height: 1rem;
  }

  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    font-size: 0.875rem;
  }
`;

export const HeroPrice = styled.p<{ $textColor: string }>`
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5rem;
  color: ${({ $textColor }) => $textColor};

  @media ${compactTablet} {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
`;

// "Current account" tab — absolutely positioned below the hero card.
// Does not affect document flow so all cards have the same hero height.
export const CurrentAccountTab = styled.div`
  position: absolute;
  bottom: -1.75rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 0;
  width: 75%;
  padding: 0.375rem 0.5rem; 
  border-radius: 0 0 0.75rem 0.75rem;
  background: #e2e4e7;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1rem;
  color: #384553;
`;
